import { HandSeal } from "./Hand";
import Sheet from "./Sheet";
import Countdown from "./Countdown";
import type { Order, Product, ProductBrief, Receipt } from "../types";
import { categoryLabel } from "../types";

// Бумажный документ магазина: чек об оплате и талон брони.
//
// Раскладка взята с листа языка («ценник · с печатью»): крупное имя вещи
// рисованным шрифтом, под ним размер и артикул микро-капсом, затем сумма и
// круглая печать. Подчёркивания под ценой здесь намеренно нет: на листе это
// вторая, альтернативная раскладка — рука на документе допускается ОДНА, и
// здесь её роль играет печать.

const money = (v: number) => `${Math.round(v).toLocaleString("ru-RU")} ₽`;

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

/** Строка реквизита: подпись микро-капсом, значение — обслуживающим текстом. */
function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="voucher__row">
      <span className="micro" style={{ color: "var(--hint)" }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function Paper({
  caption, title, meta, amount, amountNote, seal, children,
}: {
  caption: string;
  title: string;
  meta: string;
  amount: string;
  amountNote?: string;
  /** 1 — ПРОДАНО (покупка), 2 — В БРОНИ (бронь и залог). */
  seal: 1 | 2;
  children?: React.ReactNode;
}) {
  return (
    <div>
      {/* Подпись над бумагой — как на листе языка над каждым носителем. */}
      <p className="micro voucher__caption">{caption}</p>
      <div className="voucher">
        <p className="voucher__title">{title}</p>
        <p className="micro voucher__meta">{meta}</p>

        <div className="voucher__amount">
          <div style={{ minWidth: 0 }}>
            <span className="voucher__price">{amount}</span>
            {amountNote && (
              <p className="micro" style={{ color: "var(--hint)", margin: "2px 0 0" }}>{amountNote}</p>
            )}
          </div>
          <HandSeal variant={seal} className="voucher__seal" />
        </div>

        {children && <div className="voucher__rows">{children}</div>}

        <p className="micro voucher__brand">Сторосилов Винтаж</p>
      </div>
    </div>
  );
}

/** Чек: продажа целиком или залог за бронь. */
export function ReceiptVoucher({ receipt }: { receipt: Receipt }) {
  const isSale = receipt.kind === "purchase";
  const online = receipt.method === "online";
  // «unknown» — заказы, выданные до того, как способ начали записывать.
  const methodLabel = {
    online: "Онлайн-оплата",
    cash: "Наличные в магазине",
    transfer: "Перевод",
    unknown: "При получении",
  }[receipt.method] ?? "При получении";
  return (
    <Paper
      caption={isSale ? (online ? "Чек · оплачено онлайн" : "Чек · оплачено в магазине") : "Чек · залог"}
      title={receipt.title ?? receipt.sku}
      meta={`${receipt.size} · ${receipt.sku}`}
      amount={money(receipt.amount)}
      amountNote={isSale ? "Оплата товара" : "Залог за бронь"}
      seal={isSale ? 1 : 2}
    >
      <Row label="Дата" value={dateTime(receipt.paid_at)} />
      <Row label="Способ" value={methodLabel} />
      {/* Номер списания есть только у онлайн-платежа: в магазине его нет,
          и строка с прочерком лишь мусорила бы документ. */}
      {online && <Row label="Чек №" value={receipt.charge_id || "—"} />}
      {receipt.deposit_credited > 0 && (
        <Row label="Зачтён залог" value={money(receipt.deposit_credited)} />
      )}
      <Row label="Заказ" value={`#${receipt.order_id}`} />
      {receipt.demo && <Row label="Режим" value="Демо-оплата, деньги не списаны" />}
    </Paper>
  );
}

/**
 * Талон брони: что отложено, до какого срока и что с залогом.
 *
 * `product` передаётся отдельно там, где заказ пришёл из POST /orders: эта ручка
 * отдаёт OrderOut без товара (связь объявлена `lazy="raise"`, и вкладывать её в
 * ответ создания заказа нельзя без отдельной загрузки).
 */
export function ReservationVoucher({ order, product }: { order: Order; product?: ProductBrief | Product }) {
  const p = product ?? order.product;
  const deposit = order.deposit_amount > 0;
  const depositNote = !deposit
    ? "Без залога"
    : order.deposit_status === "paid"
      ? `Залог ${money(order.deposit_amount)} оплачен`
      : `Залог ${money(order.deposit_amount)} не оплачен`;

  const statusLabel: Record<string, string> = {
    pending: "Ждёт выдачи",
    issued: "Выдан",
    expired: "Срок истёк",
    cancelled: "Снята",
  };

  return (
    <Paper
      caption="Талон · бронь"
      title={p.title ?? categoryLabel(p.category)}
      meta={`${p.size} · ${p.sku}`}
      amount={money(p.price)}
      amountNote={depositNote}
      seal={2}
    >
      <Row label="Бронь" value={`#${order.id}`} />
      <Row label="Статус" value={statusLabel[order.status] ?? order.status} />
      {order.expires_at && (
        <div className="voucher__row">
          {/* Countdown сам говорит «Осталось: …», поэтому подпись строки — просто «Срок». */}
          <span className="micro" style={{ color: "var(--hint)" }}>
            {order.status === "pending" ? "Срок" : "Срок был до"}
          </span>
          <span>
            {order.status === "pending"
              ? <Countdown expiresAt={order.expires_at} />
              : dateTime(order.expires_at)}
          </span>
        </div>
      )}
      {order.status === "pending" && (
        <p className="micro voucher__hint">Покажите талон менеджеру при получении</p>
      )}
    </Paper>
  );
}

/** Чек шторкой — открывается из заказа у клиента и из архива у менеджера. */
export function ReceiptSheet({ receipt, onClose }: { receipt: Receipt; onClose: () => void }) {
  return (
    <Sheet open onClose={onClose}>
      <ReceiptVoucher receipt={receipt} />
      <div style={{ height: 14 }} />
      <button className="btn" onClick={onClose}>Готово</button>
    </Sheet>
  );
}

/** Талон брони шторкой. */
export function ReservationSheet({ order, product, onClose }: { order: Order; product?: ProductBrief | Product; onClose: () => void }) {
  return (
    <Sheet open onClose={onClose}>
      <ReservationVoucher order={order} product={product} />
      <div style={{ height: 14 }} />
      <button className="btn" onClick={onClose}>Готово</button>
    </Sheet>
  );
}
